import type { FC, ReactNode } from "react";
import {
  Flex,
  Text,
  Icon,
  getPluralOrSingularTranslation,
  ValueParser,
  type ClassNameProp,
  type HouseRecord,
} from "@huub-web/ui";
import { mergeClasses } from "@huub-web/utils";

import { ImageCard } from "../ImageCard";

import {
  containerStyle,
  contentStyle,
  headerContainerStyle,
  headerStyle,
  shadowStyle,
} from "./HouseDisplayCard.css";

export type HouseDisplayCardProps = ClassNameProp & {
  title?: string;
  record: HouseRecord;
  trailing?: ReactNode;
  leading?: ReactNode;
  titleTrailing?: ReactNode;
};

export const HouseDisplayCard: FC<HouseDisplayCardProps> = ({
  record,
  title,
  className,
  trailing,
  leading,
  titleTrailing,
}) => (
  <Flex direction="column" gap="s">
    <Flex className={headerContainerStyle}>
      <Flex
        className={headerStyle({ hasImage: !!record.image })}
        gap="xs"
        grow
        direction="column"
      >
        {leading}

        {title && (
          <Text as="h2" variant="displayS" bold className={shadowStyle}>
            {title}
          </Text>
        )}
        <Text as="h2" variant="displayM" bold className={shadowStyle}>
          {record.street}, {record.city}
        </Text>

        <Flex gap="xs" align="center" wrap className={shadowStyle}>
          {!!record.livingAreaSize && record.livingAreaSize > 0 && (
            <Flex gap="xxs" align="center">
              <Icon icon="objectLivingAreaSize" />
              <Text bold ellipsis>
                <ValueParser
                  value={{
                    type: "squareMeter",
                    value: record.livingAreaSize,
                  }}
                />{" "}
                wonen
              </Text>
            </Flex>
          )}
          {!!record.numberOfRooms && record.numberOfRooms > 0 && (
            <Flex gap="xxs" align="center">
              <Icon icon="doors" />
              <Text bold ellipsis>
                {record.numberOfRooms}{" "}
                {getPluralOrSingularTranslation(
                  record.numberOfRooms,
                  "kamer",
                  "kamers",
                )}
              </Text>
            </Flex>
          )}
          {!!record.energyLabel &&
            !["niet verplicht", "onbekend"].includes(
              record.energyLabel.toLowerCase(),
            ) && (
              <Flex gap="xxs" align="center">
                <Icon icon="objectEnergyLabel" />
                <Text bold ellipsis>
                  {record.energyLabel}
                </Text>
              </Flex>
            )}
        </Flex>
      </Flex>

      {!!titleTrailing && (
        <Flex gap="xxs" wrap justify="flex-end">
          {titleTrailing}
        </Flex>
      )}
    </Flex>
    <ImageCard
      src={record.image}
      alt={record.street}
      padding="s"
      className={mergeClasses(
        containerStyle({ hasImage: !!record.image }),
        className,
      )}
      contentClassName={contentStyle({ hasImage: !!record.image })}
      justifyContent="flex-end"
      imagePosition="bottom"
      trailing={trailing}
    />
  </Flex>
);
